import React, { useState, useEffect } from 'react';
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  XMarkIcon,
  CalendarIcon,
  BanknotesIcon
} from '@heroicons/react/24/outline';
import type { CampaignFilters, CampaignStatus, CampaignType, AdDisplayType } from '../../types/campaign';

interface CampaignFiltersProps {
  filters: CampaignFilters;
  onFiltersChange: (filters: Partial<CampaignFilters>) => void;
  onClearFilters: () => void;
}

const CampaignFiltersComponent: React.FC<CampaignFiltersProps> = ({
  filters,
  onFiltersChange,
  onClearFilters
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [localFilters, setLocalFilters] = useState<Partial<CampaignFilters>>(filters);

  // Update local filters when props change
  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const handleFilterChange = (key: keyof CampaignFilters, value: any) => {
    const newFilters = { ...localFilters, [key]: value };
    setLocalFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const handleClearFilters = () => {
    setLocalFilters({});
    onClearFilters();
  };

  const hasActiveFilters = Object.keys(filters).some(key => 
    key !== 'page' && key !== 'limit' && key !== 'sortBy' && key !== 'sortOrder' && 
    filters[key as keyof CampaignFilters] !== undefined
  );

  const statusOptions: { value: CampaignStatus; label: string }[] = [
    { value: 'draft', label: 'Taslak' },
    { value: 'active', label: 'Aktif' },
    { value: 'paused', label: 'Duraklatıldı' },
    { value: 'completed', label: 'Tamamlandı' }
  ];

  const campaignTypeOptions: { value: CampaignType; label: string }[] = [
    { value: 'standard', label: 'Standart' },
    { value: 'premium', label: 'Premium' },
    { value: 'sponsored', label: 'Sponsorlu' }
  ];

  const displayTypeOptions: { value: AdDisplayType; label: string }[] = [
    { value: 'banner', label: 'Banner' },
    { value: 'l_banner', label: 'L-Banner' },
    { value: 'square_banner', label: 'Kare Banner' },
    { value: 'bottom_banner', label: 'Alt Banner' },
    { value: 'video_overlay', label: 'Video Overlay' },
    { value: 'third_party', label: 'Üçüncü Taraf' },
    { value: 'full_page', label: 'Tam Sayfa' }
  ];

  return (
    <div className="bg-white shadow rounded-lg">
      {/* Search Bar */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center space-x-4">
          <div className="flex-1 relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Kampanya ara..."
              value={localFilters.search || ''}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            />
          </div>
          
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className={`inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
              hasActiveFilters ? 'ring-2 ring-blue-500 border-blue-500' : ''
            }`}
          >
            <FunnelIcon className="h-4 w-4 mr-2" />
            Filtreler
            {hasActiveFilters && (
              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                Aktif
              </span>
            )}
          </button>

          {hasActiveFilters && (
            <button
              onClick={handleClearFilters}
              className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Advanced Filters */}
      {isExpanded && (
        <div className="p-4 border-b border-gray-200 bg-gray-50">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {/* Status Filter */}
            <div>
              <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
                Durum
              </label>
              <select
                id="status"
                value={localFilters.status || ''}
                onChange={(e) => handleFilterChange('status', e.target.value || undefined)}
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              >
                <option value="">Tümü</option>
                {statusOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Campaign Type Filter */}
            <div>
              <label htmlFor="campaignType" className="block text-sm font-medium text-gray-700 mb-1">
                Kampanya Tipi
              </label>
              <select
                id="campaignType"
                value={localFilters.campaignType || ''}
                onChange={(e) => handleFilterChange('campaignType', e.target.value || undefined)}
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              >
                <option value="">Tümü</option>
                {campaignTypeOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Display Type Filter */}
            <div>
              <label htmlFor="displayType" className="block text-sm font-medium text-gray-700 mb-1">
                Görüntü Tipi
              </label>
              <select
                id="displayType"
                value={localFilters.displayType || ''}
                onChange={(e) => handleFilterChange('displayType', e.target.value || undefined)}
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              >
                <option value="">Tümü</option>
                {displayTypeOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Impression Limits Filter */}
            <div>
              <label htmlFor="hasImpressionLimits" className="block text-sm font-medium text-gray-700 mb-1">
                Gösterim Limiti
              </label>
              <select
                id="hasImpressionLimits"
                value={localFilters.hasImpressionLimits?.toString() || ''}
                onChange={(e) => handleFilterChange('hasImpressionLimits', 
                  e.target.value === '' ? undefined : e.target.value === 'true'
                )}
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              >
                <option value="">Tümü</option>
                <option value="true">Var</option>
                <option value="false">Yok</option>
              </select>
            </div>
          </div>

          {/* Date Range Filters */}
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="startDateFrom" className="block text-sm font-medium text-gray-700 mb-1">
                <CalendarIcon className="inline h-4 w-4 mr-1" />
                Başlangıç Tarihi (En Erken)
              </label>
              <input
                type="date"
                id="startDateFrom"
                value={localFilters.startDateFrom || ''}
                onChange={(e) => handleFilterChange('startDateFrom', e.target.value || undefined)}
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>

            <div>
              <label htmlFor="startDateTo" className="block text-sm font-medium text-gray-700 mb-1">
                <CalendarIcon className="inline h-4 w-4 mr-1" />
                Başlangıç Tarihi (En Geç)
              </label>
              <input
                type="date"
                id="startDateTo"
                value={localFilters.startDateTo || ''}
                onChange={(e) => handleFilterChange('startDateTo', e.target.value || undefined)}
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
          </div>

          {/* Budget Range Filters */}
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="budgetMin" className="block text-sm font-medium text-gray-700 mb-1">
                <BanknotesIcon className="inline h-4 w-4 mr-1" />
                Minimum Bütçe
              </label>
              <input
                type="number"
                id="budgetMin"
                placeholder="0"
                value={localFilters.budgetMin || ''}
                onChange={(e) => handleFilterChange('budgetMin', 
                  e.target.value ? parseFloat(e.target.value) : undefined
                )}
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>

            <div>
              <label htmlFor="budgetMax" className="block text-sm font-medium text-gray-700 mb-1">
                <BanknotesIcon className="inline h-4 w-4 mr-1" />
                Maksimum Bütçe
              </label>
              <input
                type="number"
                id="budgetMax"
                placeholder="∞"
                value={localFilters.budgetMax || ''}
                onChange={(e) => handleFilterChange('budgetMax', 
                  e.target.value ? parseFloat(e.target.value) : undefined
                )}
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
          </div>

          {/* Sort Options */}
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="sortBy" className="block text-sm font-medium text-gray-700 mb-1">
                Sıralama Kriteri
              </label>
              <select
                id="sortBy"
                value={localFilters.sortBy || 'createdAt'}
                onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              >
                <option value="createdAt">Oluşturulma Tarihi</option>
                <option value="updatedAt">Güncellenme Tarihi</option>
                <option value="name">İsim</option>
                <option value="startDate">Başlangıç Tarihi</option>
                <option value="budget">Bütçe</option>
              </select>
            </div>

            <div>
              <label htmlFor="sortOrder" className="block text-sm font-medium text-gray-700 mb-1">
                Sıralama Yönü
              </label>
              <select
                id="sortOrder"
                value={localFilters.sortOrder || 'desc'}
                onChange={(e) => handleFilterChange('sortOrder', e.target.value as 'asc' | 'desc')}
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              >
                <option value="desc">Azalan</option>
                <option value="asc">Artan</option>
              </select>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CampaignFiltersComponent;